import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { StyleSheet, View, Pressable, Platform, Modal as RNModal } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import BannerCountdown from '@/components/Banner/BannerCountdown';
import { useModalStore } from '@/store';
import { isDarkTheme, getBannerAccent } from '@/constants/Colors';
import { BORDER_RADIUS } from '@/constants/config';
import { trackEvent } from '@/constants/utils/analytics';
import { executeBannerCta } from '@/lib/bannerActions';
import { bannerCardShadow, bannerFallbackIcon, webInteractive, withAlpha } from '@/components/Banner/bannerChrome';

const createStyles = (colors, isDark, accent) => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.58)' : 'rgba(15, 23, 42, 0.42)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    ...(Platform.OS === 'web' ? {
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
    } : {}),
  },
  card: {
    backgroundColor: colors.solid_color_primary,
    borderRadius: BORDER_RADIUS.large,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    padding: 24,
    paddingTop: 22,
    maxWidth: 420,
    width: '100%',
    gap: 14,
    position: 'relative',
    overflow: 'hidden',
    ...bannerCardShadow(isDark),
    ...(Platform.OS === 'web' && accent
      ? {
          backgroundImage: `linear-gradient(160deg, ${withAlpha(accent, isDark ? 0.14 : 0.07)} 0%, transparent 42%)`,
        }
      : {}),
  },
  accentRail: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
    backgroundColor: accent,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  iconWell: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: withAlpha(accent, isDark ? 0.16 : 0.14),
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerCopy: {
    flex: 1,
    gap: 6,
    minWidth: 0,
    paddingTop: 1,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: BORDER_RADIUS.round,
    backgroundColor: withAlpha(accent, isDark ? 0.16 : 0.12),
    alignSelf: 'flex-start',
  },
  headline: {
    lineHeight: 26,
  },
  subtitle: {
    lineHeight: 22,
  },
  dismissBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    ...webInteractive,
  },
  dismissBtnHover: {
    backgroundColor: colors.solid_color_tertiary,
  },
  ctaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 4,
  },
  dismissLink: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: BORDER_RADIUS.small,
    backgroundColor: 'transparent',
    ...webInteractive,
  },
  dismissLinkHover: {
    backgroundColor: colors.solid_color_tertiary,
  },
  promoCodePill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: BORDER_RADIUS.small + 2,
    backgroundColor: withAlpha(accent, isDark ? 0.16 : 0.08),
    borderWidth: 1,
    borderColor: withAlpha(accent, isDark ? 0.32 : 0.2),
    borderStyle: 'dashed',
    alignSelf: 'flex-start',
    ...webInteractive,
  },
  promoCodePillHover: {
    backgroundColor: withAlpha(accent, isDark ? 0.2 : 0.14),
    borderColor: withAlpha(accent, isDark ? 0.45 : 0.32),
  },
  countdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: BORDER_RADIUS.round,
    backgroundColor: colors.solid_color_tertiary,
    alignSelf: 'flex-start',
  },
});

function BannerModal({ banner, onMarkSeen, onCtaClick, onDismiss }) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const router = useRouter();
  const { openModal } = useModalStore(state => state.actions);
  const accent = useMemo(
    () => getBannerAccent(banner.contentType, isDark, colors),
    [banner.contentType, isDark, colors],
  );
  const styles = useMemo(() => createStyles(colors, isDark, accent), [colors, isDark, accent]);
  const [visible, setVisible] = useState(true);

  const { id, contentType, content, isDismissible, countdown, promoCode } = banner;
  const iconName = content.icon || bannerFallbackIcon(contentType);

  useEffect(() => {
    onMarkSeen?.(id);
  }, [id, onMarkSeen]);

  const handleCTA = useCallback(() => {
    trackEvent('conversion', 'banner_cta_clicked', 'modal', id);
    onCtaClick?.(id);
    setVisible(false);

    setTimeout(() => {
      executeBannerCta(banner, { router, openModal });
      onDismiss?.(id);
    }, 300);
  }, [id, banner, router, openModal, onDismiss, onCtaClick]);

  const handleDismiss = useCallback(() => {
    trackEvent('engagement', 'banner_dismissed', 'modal', id);
    setVisible(false);
    setTimeout(() => onDismiss?.(id), 200);
  }, [id, onDismiss]);

  if (!visible) return null;

  const modalContent = (
    <View style={styles.overlay}>
      <Pressable onPress={isDismissible ? handleDismiss : undefined} style={StyleSheet.absoluteFill} />
      <View style={styles.card} accessibilityRole="alert">
        <View style={styles.accentRail} />
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <View style={styles.iconWell}>
              <Icon name={iconName} size={16} color={accent} solid />
            </View>
            <View style={styles.headerCopy}>
              {!!content.badgeText && (
                <View style={styles.badge}>
                  <Text size="xxs" weight="bold" style={{ color: accent, letterSpacing: 0.4 }}>
                    {content.badgeText}
                  </Text>
                </View>
              )}
              {!!content.headline && (
                <Text size="xl" weight="bold" color="primary" style={styles.headline}>
                  {content.headline}
                </Text>
              )}
            </View>
          </View>
          {isDismissible && (
            <Pressable
              onPress={handleDismiss}
              accessibilityRole="button"
              accessibilityLabel="Dismiss"
              hitSlop={6}
              style={({ hovered, pressed }) => [
                styles.dismissBtn,
                Platform.OS === 'web' && hovered && styles.dismissBtnHover,
                pressed && { opacity: 0.85 },
              ]}
            >
              <Icon name="xmark" size={12} color={colors.text_color_tertiary} />
            </Pressable>
          )}
        </View>

        {!!content.subtitle && (
          <Text size="n" color="secondary" style={styles.subtitle}>
            {content.subtitle}
          </Text>
        )}

        {promoCode?.code && (
          <Pressable
            onPress={handleCTA}
            accessibilityRole="button"
            accessibilityLabel={`Apply offer, code ${promoCode.code}`}
            style={({ hovered, pressed }) => [
              styles.promoCodePill,
              Platform.OS === 'web' && hovered && styles.promoCodePillHover,
              pressed && { opacity: 0.9 },
            ]}
          >
            <Icon name="tag" size={13} color={accent} />
            <Text size="n" weight="bold" style={{ color: accent }}>
              {promoCode.code}
            </Text>
          </Pressable>
        )}

        {countdown?.enabled && countdown?.endsAt && (
          <View style={styles.countdownRow}>
            <Icon name="clock" size={13} color={colors.text_color_tertiary} />
            <BannerCountdown
              endsAt={countdown.endsAt}
              onExpired={handleDismiss}
              textColor={colors.text_color_secondary}
            />
          </View>
        )}

        <View style={styles.ctaRow}>
          {isDismissible && (
            <Pressable
              onPress={handleDismiss}
              accessibilityRole="button"
              accessibilityLabel="No thanks"
              style={({ hovered, pressed }) => [
                styles.dismissLink,
                Platform.OS === 'web' && hovered && styles.dismissLinkHover,
                pressed && { opacity: 0.8 },
              ]}
            >
              <Text size="s" color="tertiary">No thanks</Text>
            </Pressable>
          )}
          {!!content.ctaText && (
            <Button
              text={content.ctaText}
              variant={contentType === 'promo-code' ? 'plus' : 'primary'}
              onPress={handleCTA}
              style={{ flex: 1 }}
            />
          )}
        </View>
      </View>
    </View>
  );

  if (Platform.OS === 'web') {
    return (
      <View style={[StyleSheet.absoluteFill, { zIndex: 9999 }]}>
        {modalContent}
      </View>
    );
  }

  return (
    <RNModal visible={visible} transparent animationType="fade" onRequestClose={handleDismiss}>
      {modalContent}
    </RNModal>
  );
}

export default React.memo(BannerModal);
